import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { DateTime, IANAZone } from "luxon";
import { gateway } from "../../gateway";

export type ScheduleConflict = {
  blockUid: string;
  kind: "ABSENCE" | "BREAK";
  workforceUid: string;
  workerName: string;
  visitUid: string;
  jobId: number;
  startDate: string;
  endDate: string;
};

const loadCompanyConflicts = async (startAt: string, endAt: string): Promise<ScheduleConflict[]> => {
  const response = await gateway.get("/workforce/schedule-conflicts", { params: { startAt, endAt } });
  return response.data?.data || [];
};

export function ScheduleConflictsPanel({
  startAt, endAt, timeZone, onSelectVisit, loadConflicts = loadCompanyConflicts,
}: {
  startAt: string;
  endAt: string;
  timeZone: string;
  onSelectVisit: (conflict: ScheduleConflict) => void;
  loadConflicts?: (startAt: string, endAt: string) => Promise<ScheduleConflict[]>;
}) {
  const zone = IANAZone.isValidZone(timeZone) ? timeZone : "UTC";
  const rangeMs = new Date(endAt).getTime() - new Date(startAt).getTime();
  const validRange = Number.isFinite(rangeMs) && rangeMs > 0 && rangeMs <= 93 * 86400000;
  const conflicts = useQuery<ScheduleConflict[]>({
    queryKey: ["company-schedule-conflicts", startAt, endAt],
    enabled: validRange,
    queryFn: () => loadConflicts(startAt, endAt),
  });

  if (!validRange) return <Alert severity="info" sx={{ mb: 2 }}>Choose a period of 93 days or less to review schedule conflicts.</Alert>;
  if (conflicts.isError) return <Alert severity="error" sx={{ mb: 2 }}>Could not load scheduling conflicts. Refresh the agenda to try again.</Alert>;
  if (!conflicts.data?.length) return null;

  const formatStart = (value: string) => DateTime.fromISO(value, { setZone: true }).setZone(zone).toFormat("LLL d · h:mm a");
  return (
    <Paper variant="outlined" sx={{ mb: 2, p: 2, borderColor: "warning.main" }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Scheduling conflicts · {conflicts.data.length}
      </Typography>
      <Stack spacing={1}>
        {conflicts.data.slice(0, 8).map((conflict) => (
          <Box key={`${conflict.blockUid}:${conflict.visitUid}`} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="body2">
              {conflict.workerName} · {conflict.kind === "ABSENCE" ? "Absence" : "Break"} · Job #{conflict.jobId} · {formatStart(conflict.startDate)}
            </Typography>
            <Button size="small" onClick={() => onSelectVisit(conflict)}>Show visit</Button>
          </Box>
        ))}
        {conflicts.data.length > 8 && <Typography variant="body2">And {conflicts.data.length - 8} more conflicts in this period.</Typography>}
      </Stack>
    </Paper>
  );
}
